import { eventChannel } from 'redux-saga';
import { select, call, put, take, fork, takeEvery } from 'redux-saga/effects';
import debounce from 'lodash/debounce';
import apiCreate from 'services/api';
import {
  getDistance,
  watchLocation,
  clearWatch,
  getCurrentLocation,
} from 'utils/geolocation';
import { getHistory } from 'utils/history';
import { BackgroundMode } from '@awesome-cordova-plugins/background-mode';
import { LOYALTY_JOIN } from 'pages/Loyalty';
import api_v2 from 'services/api_v2';
import { isAndroidVersion } from 'utils/platform';
import storage from 'utils/storage';
import { loadPromotions } from './PromotionSaga';

import AppActions, { AppSelectors } from './AppRedux';
import PayActions, { PaySelectors } from './PayRedux';
import { AuthSelectors } from './AuthRedux';
import { getStore } from './store';

const TIPS_PERCENT = 20;

export function* loadPaymentMethods() {
  try {
    const token = yield select(AuthSelectors.selectToken);

    const api = api_v2(token);
    const resp = yield call(api.loadCards);

    console.log('Payment card list: ', resp);

    yield put(PayActions.setPaymentMethods(resp));

    const defaultCard = resp.find((d) => d.isDefault) || resp[0];

    if (defaultCard) {
      yield put(PayActions.setCurrentMethod(defaultCard._id));
    }
  } catch (e) {
    yield put(
      AppActions.setToast(
        e.message || 'There was problem loading payment methods',
        'danger'
      )
    );
  }
}

export function* applyPromotion(actions) {
  const { promotionId } = actions;
  const currentTicket = yield select(PaySelectors.selectTicket);
  const token = yield select(AuthSelectors.selectToken);
  const apiV2 = api_v2(token);

  try {
    yield put(PayActions.setLoadingTicket(true));

    const discount = yield call(apiV2.redeemPromotion, promotionId, currentTicket.venueId);
    const resp = yield call(
      apiV2.applyDiscount,
      discount._id,
      currentTicket.checks[0]._id /* assume current check is 0 since split is not implemented */
      // TODO: @olek send itemId here
    );

    yield put(PayActions.setTicket(resp));
  } catch (e) {
    yield put(
      AppActions.setToast(
        e.message || 'There was problem applying promotion',
        'danger'
      )
    );
  }
  yield put(PayActions.setLoadingTicket(false));
}

export function* applyOfferDiscount(actions) {
  const { discountId } = actions;
  const currentTicket = yield select(PaySelectors.selectTicket);
  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  const api = apiCreate({ token, venueId });

  try {
    yield put(PayActions.setLoadingTicket(true));
    const resp = yield call(
      api.applyOfferDiscount,
      currentTicket.id,
      discountId
    );

    if (!resp.ok) {
      throw Error(resp.data.error);
    }

    yield put(PayActions.setTicket(resp.data));
  } catch (e) {
    yield put(
      AppActions.setToast(
        e.message || 'There was problem applying offer discount',
        'danger'
      )
    );
  }
  yield put(PayActions.setLoadingTicket(false));
}

export function* setDefaultPaymentMethod(actions) {
  try {
    console.log(actions);
    const { cardId } = actions;
    const token = yield select(AuthSelectors.selectToken);
    const api = api_v2(token);

    yield call(api.setDefaultCard, cardId);

    yield put(PayActions.setCurrentMethod(cardId));
  } catch (error) {
    yield put(
      AppActions.setToast(
        error.message || 'There was problem setting default payment method',
        'danger'
      )
    );
  }
}

export function* setActivateOffer(actions) {
  const { offerId } = actions;
  try {
    const token = yield select(AuthSelectors.selectToken);
    const apiV2 = api_v2(token);
    const offer = yield call(apiV2.activateOffer, offerId);

    console.log('--- activate offer', offer);
    yield put(PayActions.setUpdateAutoOffer(offerId, true));
  } catch (e) {
    yield put(
      AppActions.setToast('There was problem activating offer item', 'danger')
    );
  }
}

export function* setDeactivateOffer(actions) {
  const { offerId } = actions;

  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  const api = apiCreate({ token, venueId });

  const resp = yield call(api.setDeactivateOffer, offerId);

  if (!resp.ok) {
    yield put(
      AppActions.setToast('There was problem deactivating offer item', 'danger')
    );
  } else {
    console.log('Set Deactivate Offer Item', resp.data);
    yield put(PayActions.setUpdateAutoOffer(offerId, false));
  }
}

export function* setRegistToken(actions) {
  const { pushToken } = actions;

  try {
    const token = yield select(AuthSelectors.selectToken);
    const apiV2 = api_v2(token);
  
    const resp = yield call(apiV2.setRegistToken, pushToken);

    console.log('Set Regist Push Token', resp);
  } catch (e) {
    yield put(
      AppActions.setToast('There was problem registing push token', 'danger')
    );
  }
}

export function* loadLoyaltyAutoOffers(programId) {
  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  console.log('----------- Venue ID:', venueId); // TODO: fix venue=null
  const api = api_v2(token);

  try {
    const data = yield call(api.getLoyaltyAutoOffers, programId);
    console.log('Loyalty Auto Offers', data);
    yield put(PayActions.setLoyaltyAutoOffers(data));
  } catch (e) {
    yield put(
      AppActions.setToast(
        'There was problem loading loyalty auto offers',
        'danger'
      )
    );
  }
}

export function* loadRewardLevels() {
  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  const api = api_v2(token);
  const programs = yield call(api.getLoyaltyPrograms);

  if (!programs) return;

  const program = programs.find((p) => p.venueIds.includes(venueId));

  if (!program) return;

  try {
    const data = yield call(api.getRewardLevels, program.id);
    console.log('My Reward levels', data);
    yield put(PayActions.setRewardLevels(data));
  } catch (e) {
    yield put(
      AppActions.setToast('There was problem loading reward levels', 'danger')
    );
  }
}

export function* loadLoyaltyPoints(action) {
  const { isLoadJoinModal } = action;

  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  const apiV2 = api_v2(token);

  try {
    const data = yield call(apiV2.getLoyaltyPrograms);
    console.log('My Loyalty Programs', data);
    yield put(PayActions.setLoyaltyPrograms(data));
  } catch (e) {
    console.log('===error on load loyalty programs', e.message);
    yield put(
      AppActions.setToast(
        'There was problem loading loyalty programs',
        'danger'
      )
    );
  }

  try {
    const data = yield call(apiV2.getLoyaltyPoints);
    console.log('My Loyalty Points', data);
    yield put(PayActions.setLoyaltyPoints(data));

    if (isLoadJoinModal) {
      const loyaltyPrograms = yield select(PaySelectors.selectLoyaltyPrograms);

      const filterLoyaltyPrograms = loyaltyPrograms?.filter((item) =>
        item?.venueIds?.some((id) => id === venueId)
      );

      if (filterLoyaltyPrograms.length > 0) {
        const programId = filterLoyaltyPrograms[0].id;
        const filterLoyaltyPoints = data.filter(
          (item) => item.programId === programId
        );

        if (!filterLoyaltyPoints.length) {
          yield put(PayActions.setLoyaltyTypeModal(LOYALTY_JOIN));
        }
      }
    }
  } catch (e) {
    console.log('===error on load loyalty points');
    yield put(
      AppActions.setToast('There was problem loading loyalty points', 'danger')
    );
  }
}

export function* setOptInProgram(actions) {
  const { programId } = actions;

  const token = yield select(AuthSelectors.selectToken);
  const apiV2 = api_v2(token);

  try {
    const data = yield call(apiV2.optIntoProgram, programId);
    console.log('Opt In Program', data);
    yield put(
      AppActions.setToast('You joined to this program successfully.', 'success')
    );
  } catch (e) {
    console.log('===error on opt in program');
    yield put(
      AppActions.setToast(
        e.message || 'There was problem joining loyalty program',
        'danger'
      )
    );
  }
}

export function* deleteLoyaltyPoint(actions) {
  const { programId } = actions;

  const token = yield select(AuthSelectors.selectToken);
  // const venueId = yield select(AppSelectors.selectVenue);

  const apiV2 = api_v2(token);

  try {
    const data = yield call(apiV2.optOutOfProgram, programId);
    console.log('Delete Loyalty Point', data);

    yield put(AppActions.setToast('Loyalty Program removed', 'success'));
    yield put(PayActions.setLoyaltyTypeModal(null));
  } catch (e) {
    console.log('===error on delete loyalty point', e);
    yield put(
      AppActions.setToast(
        e.message || 'There was problem removing loyalty program',
        'danger'
      )
    );
  }

  const history = getHistory();
  history.push('/loyalty');
}

function createGeoLocationChannel() {
  return eventChannel(async (emit) => {
    const debouncedEmit = debounce(emit, 2000);

    const waitId = await watchLocation(async (coords, err) => {
      if (err) {
        debouncedEmit(err);
      } else {
        console.log('=LOCATION=', JSON.stringify(coords));
        debouncedEmit(coords);
      }
    });

    const store = getStore();
    store.dispatch(PayActions.setWatcherIds(waitId));

    return () => {
      try {
        clearWatch(waitId);
      } catch (e) {
        console.error(e);
        console.log('===error on closing watch');
      }
    };
  });
}

export function* loadCurrentTicket() {
  const currentTicket = yield select(PaySelectors.selectTicket);
  const token = yield select(AuthSelectors.selectToken);

  if (!currentTicket) {
    return;
  }

  try {
    const api = api_v2(token);
    const resp = yield call(api.getTicketByUser);
    if (resp) {
      yield put(AppActions.setVenue(resp.venueId));
      yield put(PayActions.setTicket(resp));
    }
  } catch (e) {
    console.log('===error on load current ticket');
    console.error(e);
  }
}

function* clearWatcherIds() {
  const wacherIds = yield select(PaySelectors.selectWatcherIds);
  console.log('Watcher Ids: ', wacherIds);

  if (wacherIds.length > 0) {
    wacherIds.map((id) => clearWatch(id));
  }

  yield put(PayActions.setClearWatcherIds());

  if (isAndroidVersion) {
    if (BackgroundMode.isEnabled()) {
      BackgroundMode.disable();
    }
  }
}

function* closeTicketFunc() {
  yield put(PayActions.setTicket(null));
  yield put(AppActions.setVenue(null));
  yield put(PayActions.closeTicket());

  yield call(storage.removeItem, 'PREP_CAN_BLE_TIP');
  yield call(storage.removeItem, 'PREP_LOYALTY_JOIN_MODAL');
}

export function* makePayment(actions) {
  try {
    const { tips, cvv } = actions;
    const ticket = yield select(PaySelectors.selectTicket);
    const token = yield select(AuthSelectors.selectToken);
    const currentMethod = yield select(PaySelectors.selectCurrentMethod);
    const venue = yield select(AppSelectors.selectVenueDetail);
    const user = yield select(AuthSelectors.selectUser);

    const api = api_v2(token);
    yield put(PayActions.setPaying(true));
    const check = ticket.checks.find((c) => c.userId === user._id);
    const payload = {
      cvv: cvv === '' ? undefined : cvv,
      cardId: currentMethod,
      tip:
        tips || tips === 0
          ? tips
          : venue.automaticGratuity
            ? 0
            : Math.round((check.totalAmount * TIPS_PERCENT) / 100),
    };
    yield call(api.pay, check._id, payload);

    const history = getHistory();
    history.replace('/thankyou', { direction: 'forward' });
    yield fork(closeTicketFunc);
    yield put(PayActions.setPaying(false));

    yield fork(clearWatcherIds);
  } catch (e) {
    console.error(e);
    console.log('problem making payment');
    yield put(
      AppActions.setToast('There was problem making payment', 'danger')
    );
    yield put(PayActions.setPaying(false));

    yield fork(clearWatcherIds);
  }
}

function* watchLocationToAutoClose() {
  if (isAndroidVersion) {
    if (!BackgroundMode.isEnabled()) {
      BackgroundMode.enable();
    }
  }

  const chan = yield call(createGeoLocationChannel);

  yield takeEvery(chan, function* autoCloseProcessing(geoCoords) {
    try {
      const venueDetail = yield select(AppSelectors.selectVenueDetail);
      const isPaying = yield select(PaySelectors.selectPaying);

      if (
        !isPaying &&
        venueDetail &&
        geoCoords &&
        geoCoords.lat &&
        geoCoords.lng
      ) {

        if (venueDetail.location) {
          const distance = getDistance(
            venueDetail.location.lat,
            venueDetail.location.lng,
            geoCoords.lat,
            geoCoords.lng
          );

          yield put(AppActions.setGeoLocation(geoCoords.lat, geoCoords.lng));

          if (distance >= venueDetail.location.distance) {
            yield call(loadCurrentTicket);
            yield call(makePayment, { tips: null, portionCount: 1 });
          }
        } else {
          const distance = getDistance(
            venueDetail.lat,
            venueDetail.lng,
            geoCoords.lat,
            geoCoords.lng
          );

          yield put(AppActions.setGeoLocation(geoCoords.lat, geoCoords.lng));

          if (distance >= venueDetail.distance) {
            yield call(loadCurrentTicket);
            yield call(makePayment, { tips: null, portionCount: 1 });
          }
        }
      }
    } catch (e) {
      console.log('==auto close eror');
      console.error(e);

      yield put(AppActions.setToast(e.stack + e.message, 'danger'));
    }
  });

  yield take('CLOSE_TICKET');
  console.log('==closing ticket fork==');

  try {
    chan.close();
  } catch (e) {
    console.error(e);
    console.log('error closing channel');
  }
}

function* getCurrentTicketByUser() {
  const currentTicket = yield select(PaySelectors.selectTicket);
  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  const api = api_v2(token);

  let ticket = null;
  try {
    if (currentTicket && currentTicket.venueId === venueId) {
      ticket = yield call(api.getTicket, currentTicket._id);
    } else {
      ticket = yield call(api.getTicketByUser);
    }
  } catch (error) {
    console.log(error);
    ticket = null;
  }

  return ticket;
}

export function* loadTicket(action) {
  const { reset } = action;
  const venueDetail = yield select(AppSelectors.selectVenueDetail);

  try {
    if (reset) {
      yield put(PayActions.setTicketUsers([]));
      yield put(PayActions.setPayType('portion'));
      yield put(PayActions.setSelectedItems([]));
      yield put(PayActions.setTicket(null));
    }

    yield put(PayActions.setLoadingTicket(true));

    const ticket = yield call(getCurrentTicketByUser);
    if (!ticket || (ticket && ticket.status === 'closed')) {
      const history = getHistory();
      history.push('/locations');
      yield put(PayActions.setLoadingTicket(false));
      yield put(AppActions.setToast('Ticket is already closed', 'danger'));
      yield fork(closeTicketFunc);
      return;
    }

    yield put(PayActions.setTicket(ticket));
    yield put(PayActions.setSelectedItems([]));
    yield put(PayActions.setTicketUsers(ticket.users));
    yield call(loadPromotions, {});

    if (
      venueDetail.geoLocationEnabled &&
      process.env.REACT_APP_WATCH_LOCATION
    ) {
      yield fork(watchLocationToAutoClose);
    }
  } catch (err) {
    yield put(
      AppActions.setToast(
        err.message || 'There was problem loading ticket',
        'danger'
      )
    );
  } finally {
    yield put(PayActions.setLoadingTicket(false));
  }
}

export function* createTicket(actions) {
  const { venueId } = actions;
  const history = getHistory();

  try {
    const token = yield select(AuthSelectors.selectToken);
    const api = api_v2(token);

    const venueDetail = yield call(api.getVenue, venueId);

    if (
      venueDetail.geoLocationEnabled &&
      process.env.REACT_APP_WATCH_LOCATION
    ) {
      const geoCoords = yield call(getCurrentLocation);

      if (venueDetail && geoCoords && geoCoords.lat && geoCoords.lng) {

        if (venueDetail.location) {
          const distance = getDistance(
            venueDetail.location.lat,
            venueDetail.location.lng,
            geoCoords.lat,
            geoCoords.lng
          );
  
          if (distance > venueDetail.location.distance) {
            yield put(
              AppActions.setToast(
                `You should be inside ${venueDetail.settings.general.name} to create tab.`,
                'danger'
              )
            );
            yield put(AppActions.setVenue(null));
            yield put(PayActions.setLoadingTicket(false));
  
            history.push('/locations');
  
            return;
          }
        } else {
          const distance = getDistance(
            venueDetail.lat,
            venueDetail.lng,
            geoCoords.lat,
            geoCoords.lng
          );
  
          if (distance > venueDetail.distance) {
            yield put(
              AppActions.setToast(
                `You should be inside ${venueDetail.settings.general.name} to create tab.`,
                'danger'
              )
            );
            yield put(AppActions.setVenue(null));
            yield put(PayActions.setLoadingTicket(false));
  
            history.push('/locations');
  
            return;
          }
        }
      }
    }

    const ticket = yield call(api.createTicket, venueId, {});

    yield put(PayActions.setTicket(ticket));
    history.push(`/locations/${venueId}/tab`);
  } catch (err) {
    if (err.httpCode === 409) {
      yield call(loadTicket, { reset: true });
      history.push(`/locations/${venueId}/tab`);
      yield put(
        AppActions.setToast('User already has an open ticket.', 'danger')
      );
    } else
      yield put(
        AppActions.setToast(
          'An error occurred while creating the ticket.',
          'danger'
        )
      );
  }
}

export function* deleteTicket() {
  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  const ticket = yield select(PaySelectors.selectTicket);
  const api = apiCreate({ venueId, token });

  if (!ticket.table && ticket.items && ticket.items.length > 0) {
    yield put(
      AppActions.setToast(
        'You can not leave tab when you are ordering from the bar.',
        'danger'
      )
    );
    return;
  }

  const resp = yield call(api.deleteTicket, ticket.id);

  if (resp.ok) {
    yield put(AppActions.setToast('Tab removed', 'success'));
  } else {
    yield put(
      AppActions.setToast(
        resp.data.message || 'There was problem removing tab',
        'danger'
      )
    );
    return;
  }

  const history = getHistory();
  history.push('/locations');
  yield fork(closeTicketFunc);
}

export function* deleteCard(actions) {
  try {
    const { cardId } = actions;
    const token = yield select(AuthSelectors.selectToken);
    const methods = yield select(PaySelectors.selectPaymentMethods);
    yield put(PayActions.setDeleting(true));
    const api = api_v2(token);
    const resp = yield call(api.deleteCard, cardId);

    if (resp && resp.length > 0)
      yield put(
        PayActions.setCurrentMethod(
          resp.find((c) => c.isDefault)._id || resp[0]._id
        )
      );
    else yield put(PayActions.setCurrentMethod(null));

    yield put(
      PayActions.setPaymentMethods(methods.filter((m) => m._id !== cardId))
    );

    yield put(PayActions.setDeleting(false));
  } catch (error) {
    yield put(
      error.message || AppActions.setToast('Failed to delete method', 'danger')
    );
    yield put(PayActions.setDeleting(false));
  }
}

export function* updateSelectedItem(actions) {
  const { item, quantity } = actions;
  const selectedItems = yield select(PaySelectors.selectSelectedItems);

  const prevItems = selectedItems.filter((i) => i.id !== item.id);
  let nextItems = prevItems;

  if (quantity) {
    const price = Math.round((item.price / item.quantity) * quantity);

    nextItems = [
      ...prevItems,
      { id: item.id, name: item.name, quantity, price },
    ];
  }

  const token = yield select(AuthSelectors.selectToken);
  const venueId = yield select(AppSelectors.selectVenue);
  const currentTicket = yield select(PaySelectors.selectTicket);
  const api = apiCreate({ venueId, token });

  yield put(PayActions.setLoadingTicket(true));
  const resp = yield call(api.claimItems, currentTicket.id, nextItems);

  if (resp.ok) {
    yield put(PayActions.setSelectedItems(nextItems));
    yield put(PayActions.setTicket(resp.data));
  } else {
    yield put(
      AppActions.setToast(
        resp.data.message || 'Problem claiming items',
        'danger'
      )
    );
  }
  yield put(PayActions.setLoadingTicket(false));
}
